/* eslint-disable sonarjs/no-identical-functions */
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ServiceEnpoint } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/azure/credential-form-azure.constants';
import { StorageType } from 'mbs-ui-shared@models';
import { S3AuthType } from 'mbs-ui-shared@models/StorageAccount';
import { StorageFormName } from './credential-forms.constants';
import {
  AccountType,
  AzureCredentialForm,
  AzureVMCredentialForm,
  BackBlazeCredentialForm,
  CredentialForm,
  DomainIdentifier,
  FSCredentialForm,
  FTPCredentialForm,
  GoogleCloudCredentialForm,
  GoogleDriveCredentialForm,
  IDriveE2CredentialForm,
  ImpossibleCloudCredentialForm,
  KeystoneVersion,
  MinioCredentialForm,
  OpenstackCredentialForm,
  ProjectIdentifier,
  S3CompatibleCredentialForm,
  S3CredentialForm,
  SFTPCredentialForm,
  TenantIdentifier,
  WasabiCredentialForm
} from './credential-forms.model';

export class CredentialFormsConstructor {
  private static getAzureForm(): AzureCredentialForm {
    return new FormGroup({
      accountType: new FormControl<AccountType>(AccountType.Public, [Validators.required]),
      endpoint: new FormControl(ServiceEnpoint?.[AccountType.Public], [Validators.required]),
      accountName: new FormControl(null, [Validators.required]),
      sharedKey: new FormControl(null, [Validators.required])
    });
  }

  private static getAzureVMForm(): AzureVMCredentialForm {
    return new FormGroup({
      subscriptionId: new FormControl(null, [Validators.required]),
      refreshToken: new FormControl(null, [Validators.required]),
      tenantId: new FormControl(null, [Validators.required]),
      subscriptionDisplayName: new FormControl(null, [Validators.required])
    });
  }

  private static getBackBlazeForm(): BackBlazeCredentialForm {
    return new FormGroup({
      accountId: new FormControl(null, [Validators.required]),
      applicationKey: new FormControl(null, [Validators.required])
    });
  }

  private static getFSForm(): FSCredentialForm {
    return new FormGroup({
      user: new FormControl(null),
      password: new FormControl(null)
    });
  }

  private static getGoogleCloudForm(): GoogleCloudCredentialForm {
    return new FormGroup({
      serviceAccountEmail: new FormControl(null, [Validators.required]),
      binaryKeyInBase64: new FormControl(null, [Validators.required]),
      projectId: new FormControl(null, Validators.required)
    });
  }

  private static getMinioForm(): MinioCredentialForm {
    return new FormGroup({
      useVHostRequestStyle: new FormControl(false),
      isSyntheticBackupAllowed: new FormControl(false),
      isVersioningEnabled: new FormControl(true),
      isPrivateStorage: new FormControl(false),
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required]),
      useNativeMultipartUpload: new FormControl(true),
      httpEndpoint: new FormControl('', [Validators.pattern('^http://.{1,}')]),
      httpsEndpoint: new FormControl('', [Validators.pattern('^https://.{1,}')]),
      ignoreCertificate: new FormControl(false),
      keepCredentialsOnEndpoint: new FormControl(false)
    });
  }

  private static getS3Form(): S3CredentialForm {
    return new FormGroup({
      storageType: new FormControl<StorageType>(StorageType.AmazonS3),
      arnRole: new FormControl(null),
      isGov: new FormControl(false),
      authType: new FormControl<S3AuthType>(S3AuthType.MBS),
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required])
    });
  }

  private static getS3CompatibleForm(): S3CompatibleCredentialForm {
    return new FormGroup({
      useVHostRequestStyle: new FormControl(false),
      isSyntheticBackupAllowed: new FormControl(false),
      isVersioningEnabled: new FormControl(true),
      useSignatureVersion4: new FormControl(true),
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required]),
      useNativeMultipartUpload: new FormControl(true),
      httpEndpoint: new FormControl('', [Validators.pattern('^http://.{1,}')]),
      httpsEndpoint: new FormControl('', [Validators.pattern('^https://.{1,}')]),
      ignoreCertificate: new FormControl(false),
      keepCredentialsOnEndpoint: new FormControl(false),
      isPrivateStorage: new FormControl(false)
    });
  }

  private static getWasabiForm(): WasabiCredentialForm {
    return new FormGroup({
      arnRole: new FormControl(null),
      authType: new FormControl<S3AuthType>(S3AuthType.MBS),
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required])
    });
  }

  private static getImpossibleCloudForm(): ImpossibleCloudCredentialForm {
    return new FormGroup({
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required])
    });
  }

  private static getFTPForm(): FTPCredentialForm {
    return new FormGroup({
      user: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
      endpoint: new FormControl(null, [Validators.required]),
      useSSL: new FormControl(false),
      keepAlive: new FormControl(false),
      isPrivateStorage: new FormControl(false),
      usePassiveMode: new FormControl(false)
    });
  }

  private static getSFTPForm(): SFTPCredentialForm {
    return new FormGroup({
      user: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
      endpoint: new FormControl(null, [Validators.required]),
      keepAlive: new FormControl(false),
      isPrivateStorage: new FormControl(false)
    });
  }

  private static getOpenstackForm(): OpenstackCredentialForm {
    return new FormGroup({
      userName: new FormControl(null, [Validators.required]),
      apiKey: new FormControl(null, [Validators.required]),
      endpoint: new FormControl(null, [Validators.required]),
      keystoneVersion: new FormControl<KeystoneVersion>(KeystoneVersion.None),
      tenantIdentifier: new FormControl<TenantIdentifier>(null),
      tenant: new FormControl(null),
      domainIdentifier: new FormControl<DomainIdentifier>(null),
      domain: new FormControl(null),
      projectIdentifier: new FormControl<ProjectIdentifier>(null),
      project: new FormControl(null),
      useScope: new FormControl(false),
      useInternalUrls: new FormControl(false),
      isPrivateStorage: new FormControl(false),
      ignoreCertificates: new FormControl(false)
    });
  }

  private static getIDriveE2Form(): IDriveE2CredentialForm {
    return new FormGroup({
      accessKey: new FormControl(null, [Validators.required]),
      secretKey: new FormControl(null, [Validators.required])
    });
  }

  private static getGoogleDriveForm(): GoogleDriveCredentialForm {
    return new FormGroup({
      email: new FormControl(null),
      refreshToken: new FormControl(null)
    });
  }

  public static create(storageType: StorageType): CredentialForm {
    switch (storageType) {
      case StorageType.Azure:
        return this.getAzureForm();
      case StorageType.AzureVM:
        return this.getAzureVMForm();
      case StorageType.BackblazeB2:
        return this.getBackBlazeForm();
      case StorageType.LocalFS:
        return this.getFSForm();
      case StorageType.GoogleCloud:
        return this.getGoogleCloudForm();
      case StorageType.Google:
        return this.getGoogleCloudForm();
      case StorageType.Minio:
        return this.getMinioForm();
      case StorageType.AmazonS3:
        return this.getS3Form();
      case StorageType.S3Compatible:
        return this.getS3CompatibleForm();
      case StorageType.Wasabi:
        return this.getWasabiForm();
      case StorageType.FTP:
        return this.getFTPForm();
      case StorageType.SFTP:
        return this.getSFTPForm();
      case StorageType.OpenStack:
        return this.getOpenstackForm();
      case StorageType.IDriveE2:
        return this.getIDriveE2Form();
      case StorageType.GoogleDriveOAuth:
        return this.getGoogleDriveForm();
      case StorageType.GoogleDrive:
        return this.getGoogleDriveForm();
      case StorageType.ImpossibleCloud:
        return this.getImpossibleCloudForm();
      default:
        return null;
    }
  }

  public static getPreparedCredentialFormValue = (
    form: CredentialForm,
    storageType: StorageType,
    displayName: string,
    needResponseWithValidationResult = false
  ) => {
    const value =
      storageType === StorageType.LocalFS
        ? (form as FSCredentialForm)?.value?.user || (form as FSCredentialForm)?.value?.password
          ? form.value
          : null
        : form?.value;

    const type = storageType === StorageType.AmazonS3 ? (form as S3CredentialForm)?.value?.storageType : storageType;

    return {
      type,
      displayName,
      needResponseWithValidationResult,
      [StorageFormName[storageType]]: value
    };
  };
}
